import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, Plus, Edit2, Trash2, Save, X, ExternalLink, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

function AdminDeals() {
  const [deals, setDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    retailer: '',
    description: '',
    discount_text: '',
    original_price: '',
    sale_price: '',
    image_url: '',
    affiliate_link: '',
    valid_until: '',
    is_active: true
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchDeals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDeals = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    try {
      const response = await axios.get(`${API}/featured-deals`);
      setDeals(response.data);
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = () => {
    setIsAdding(true);
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    setFormData({
      title: '',
      retailer: '',
      description: '',
      discount_text: '',
      original_price: '',
      sale_price: '',
      image_url: '',
      affiliate_link: '',
      valid_until: nextWeek.toISOString().split('T')[0],
      is_active: true
    });
  };

  const handleEdit = (deal) => {
    setEditingId(deal.id);
    setFormData({
      title: deal.title,
      retailer: deal.retailer,
      description: deal.description,
      discount_text: deal.discount_text,
      original_price: deal.original_price || '',
      sale_price: deal.sale_price || '',
      image_url: deal.image_url,
      affiliate_link: deal.affiliate_link,
      valid_until: new Date(deal.valid_until).toISOString().split('T')[0],
      is_active: deal.is_active
    });
  };

  const handleSave = async () => {
    const token = localStorage.getItem('admin_token');
    
    const payload = {
      ...formData,
      original_price: formData.original_price ? parseFloat(formData.original_price) : null,
      sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
      valid_until: new Date(formData.valid_until).toISOString()
    };

    try {
      if (isAdding) {
        await axios.post(`${API}/admin/featured-deals`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.put(`${API}/admin/featured-deals/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      fetchDeals();
      setIsAdding(false);
      setEditingId(null);
    } catch (error) {
      alert('Error saving deal');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this deal?')) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/admin/featured-deals/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDeals();
    } catch (error) {
      alert('Error deleting deal');
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#D4AF37] text-xl">Loading...</div>
      </div>
    );
  }

  const renderForm = () => (
    <div className="grid md:grid-cols-2 gap-4">
      <div>
        <label className="block text-gray-300 mb-2">Title</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({...formData, title: e.target.value})}
          className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
          data-testid="deal-title-input"
        />
      </div>
      <div>
        <label className="block text-gray-300 mb-2">Retailer</label>
        <input
          type="text"
          value={formData.retailer}
          onChange={(e) => setFormData({...formData, retailer: e.target.value})}
          className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
          data-testid="deal-retailer-input"
        />
      </div>
      <div className="md:col-span-2">
        <label className="block text-gray-300 mb-2">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
          rows="3"
          data-testid="deal-description-input"
        />
      </div>
      <div>
        <label className="block text-gray-300 mb-2">Discount Text</label>
        <input
          type="text"
          value={formData.discount_text}
          onChange={(e) => setFormData({...formData, discount_text: e.target.value})}
          placeholder="e.g., 40% OFF"
          className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
          data-testid="deal-discount-input"
        />
      </div>
      <div>
        <label className="block text-gray-300 mb-2">Original Price ($)</label>
        <input
          type="number"
          value={formData.original_price}
          onChange={(e) => setFormData({...formData, original_price: e.target.value})}
          className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
          data-testid="deal-original-price-input"
        />
      </div>
      <div>
        <label className="block text-gray-300 mb-2">Sale Price ($)</label>
        <input
          type="number"
          value={formData.sale_price}
          onChange={(e) => setFormData({...formData, sale_price: e.target.value})}
          className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
          data-testid="deal-sale-price-input"
        />
      </div>
      <div>
        <label className="block text-gray-300 mb-2">Image URL</label>
        <input
          type="url"
          value={formData.image_url}
          onChange={(e) => setFormData({...formData, image_url: e.target.value})}
          className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
          placeholder="https://..."
          data-testid="deal-image-input"
        />
      </div>
      <div>
        <label className="block text-gray-300 mb-2">Affiliate Link</label>
        <input
          type="url"
          value={formData.affiliate_link}
          onChange={(e) => setFormData({...formData, affiliate_link: e.target.value})}
          className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
          placeholder="https://..."
          data-testid="deal-link-input"
        />
      </div>
      <div>
        <label className="block text-gray-300 mb-2">Valid Until</label>
        <input
          type="date"
          value={formData.valid_until}
          onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
          className="w-full px-4 py-2 bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
          data-testid="deal-valid-until-input"
        />
      </div>
      <div className="flex items-center">
        <input
          type="checkbox"
          checked={formData.is_active}
          onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
          className="mr-2"
          data-testid="deal-active-checkbox"
        />
        <label className="text-gray-300">Active</label>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-deals-page">
      {/* Header */}
      <header className="bg-[#0a0a0a] border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-gray-400 hover:text-[#D4AF37] transition-colors"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center space-x-2">
                <Package className="w-6 h-6 text-[#D4AF37]" />
                <h1 className="text-2xl font-serif font-bold text-[#D4AF37]">Manage Featured Deals</h1>
              </div>
            </div>
            <button
              onClick={handleAdd}
              className="px-4 py-2 bg-[#D4AF37] text-[#050505] rounded-lg font-semibold hover:bg-[#C5A028] transition-all flex items-center space-x-2"
              data-testid="add-deal-btn"
            >
              <Plus className="w-5 h-5" />
              <span>Add Deal</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Add Form */}
        {isAdding && (
          <div className="mb-6 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-xl border border-[#D4AF37]/20 p-6" data-testid="add-deal-form">
            <h3 className="text-xl font-bold text-[#D4AF37] mb-4">Add New Deal</h3>
            {renderForm()}
            <div className="flex space-x-4 mt-6">
              <button
                onClick={handleSave}
                className="px-6 py-2 bg-[#D4AF37] text-[#050505] rounded-lg font-semibold hover:bg-[#C5A028] transition-all flex items-center space-x-2"
                data-testid="save-deal-btn"
              >
                <Save className="w-5 h-5" />
                <span>Save</span>
              </button>
              <button
                onClick={handleCancel}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-all flex items-center space-x-2"
                data-testid="cancel-btn"
              >
                <X className="w-5 h-5" />
                <span>Cancel</span>
              </button>
            </div>
          </div>
        )}

        {/* Deals List */}
        <div className="grid md:grid-cols-2 gap-6">
          {deals.map((deal, index) => (
            <div
              key={deal.id}
              className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-xl border border-[#D4AF37]/20 p-6"
              data-testid={`deal-card-${index}`}
            >
              {editingId === deal.id ? (
                // Edit Mode
                <div>
                  {renderForm()}
                  <div className="flex space-x-4 mt-6">
                    <button
                      onClick={handleSave}
                      className="px-6 py-2 bg-[#D4AF37] text-[#050505] rounded-lg font-semibold hover:bg-[#C5A028] transition-all flex items-center space-x-2"
                    >
                      <Save className="w-5 h-5" />
                      <span>Save</span>
                    </button>
                    <button
                      onClick={handleCancel}
                      className="px-6 py-2 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-all flex items-center space-x-2"
                    >
                      <X className="w-5 h-5" />
                      <span>Cancel</span>
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  <div className="relative mb-4 rounded-lg overflow-hidden">
                    <img src={deal.image_url} alt={deal.title} className="w-full h-48 object-cover" />
                    <div className="absolute top-2 right-2 bg-[#D4AF37] text-[#050505] px-3 py-1 rounded-lg font-bold">
                      {deal.discount_text}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-xl font-bold text-[#D4AF37]">{deal.title}</h3>
                    {!deal.is_active && (
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded">Inactive</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400 mb-2">{deal.retailer}</p>
                  <p className="text-gray-300 mb-3">{deal.description}</p>
                  <div className="flex items-baseline space-x-2 mb-3">
                    <span className="text-2xl font-bold text-[#D4AF37]">${deal.sale_price?.toLocaleString()}</span>
                    <span className="text-lg text-gray-500 line-through">${deal.original_price?.toLocaleString()}</span>
                  </div>
                  <a
                    href={deal.affiliate_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#D4AF37] hover:underline flex items-center space-x-1 mb-2"
                  >
                    <span>View affiliate link</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <p className="text-sm text-gray-500 mb-4">
                    Valid until: {new Date(deal.valid_until).toLocaleDateString()}
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(deal)}
                      className="flex-1 py-2 bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 border border-[#D4AF37]/30 rounded-lg transition-all flex items-center justify-center space-x-2"
                      data-testid={`edit-deal-btn-${index}`}
                    >
                      <Edit2 className="w-5 h-5 text-[#D4AF37]" />
                      <span className="text-[#D4AF37]">Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(deal.id)}
                      className="flex-1 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg transition-all flex items-center justify-center space-x-2"
                      data-testid={`delete-deal-btn-${index}`}
                    >
                      <Trash2 className="w-5 h-5 text-red-400" />
                      <span className="text-red-400">Delete</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {deals.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>No deals yet. Click "Add Deal" to create your first featured deal.</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminDeals;
